import React, { FC } from "react";

import { copy } from "shared/assets";
import { Band } from "shared/assets/icons";
import {
  Card,
  EditDeleteLinks,
  FancyButtonLink,
  Message,
  Title,
} from "shared/components";
import { Spacer } from "shared/layout";
import { IStatus } from "shared/types";

import {
  DeleteMemberModal,
  EditUserBandInfoModal,
  ExportICalButton,
  ExportICalModal,
} from "Bands/containers";
import { useBandContext } from "Bands/contexts";
import { IBand, IBandUser } from "Bands/types";
import { useState } from "react";
import {
  MutedDarkText,
  MutedDarkTextWithMargin,
  MutedText,
  TinyText,
} from "shared/styles";

const {
  profile: { profileIds, bandDetails },
} = copy;

export const BandDetails: FC = () => {
  const [showICalModalForBand, setShowICalModalForBand] =
    useState<IBand | null>();

  const [showEditBandInfoModal, setShowEditBandInfoModal] =
    useState<Pick<IBand, "id" | "title"> | boolean>(false);

  const [showLeaveBandModal, setShowLeaveBandModal] =
    useState<{ band: Pick<IBand, "id" | "title">; user: IBandUser } | null>(
      null
    );

  const { myBandUsers, openCreateBandModal, getMyBandUser } = useBandContext();

  const userBands = myBandUsers?.map(({ band }) => band);

  const renderBand = (band: IBand) => {
    const { id, title } = band;

    const bandUser = getMyBandUser(band.id);

    return (
      <Spacer size="s">
        <Title size="s" title={title} />

        <Spacer size="xxs" spaceDirection="horizontal" align="center">
          <MutedDarkTextWithMargin>
            {bandDetails.bandInfoFormFields.nickname}:
          </MutedDarkTextWithMargin>
          <MutedText>
            {bandUser?.nickname || bandDetails.messages.notProvided}
          </MutedText>
          <MutedDarkText as={TinyText}>•</MutedDarkText>
          <MutedDarkText>
            {bandDetails.bandInfoFormFields.function}:
          </MutedDarkText>
          <MutedText>
            {bandUser?.function || bandDetails.messages.notProvided}
          </MutedText>
        </Spacer>

        {id && (
          <ExportICalButton handleClick={() => setShowICalModalForBand(band)} />
        )}

        <EditDeleteLinks
          editCopy={bandDetails.actions.editBandInfo}
          editTestId="edit_band_info"
          handleEdit={() => setShowEditBandInfoModal({ title, id })}
          deleteCopy={bandDetails.actions.leaveBand}
          deleteTestId="leave_band"
          handleDelete={() => {
            if (!bandUser) return;

            setShowLeaveBandModal({ band, user: bandUser });
          }}
        />
      </Spacer>
    );
  };

  return (
    <Card id={profileIds.bands} title={bandDetails.title} icon={Band}>
      {showICalModalForBand && (
        <ExportICalModal
          handleClose={() => setShowICalModalForBand(null)}
          band={showICalModalForBand}
        />
      )}
      <Spacer size="xl">
        {!userBands?.length && (
          <Message status={IStatus.warning}>
            <FancyButtonLink
              onClick={openCreateBandModal}
              status={IStatus.warning}
            >
              {bandDetails.empty}
            </FancyButtonLink>
          </Message>
        )}
        {userBands?.map((band) => (
          <React.Fragment key={(band as unknown as IBand).id}>
            {renderBand(band as unknown as IBand)}
          </React.Fragment>
        ))}
      </Spacer>
      {showEditBandInfoModal && showEditBandInfoModal !== true && (
        <EditUserBandInfoModal
          band={showEditBandInfoModal}
          handleClose={() => setShowEditBandInfoModal(false)}
        />
      )}
      {showLeaveBandModal && (
        <DeleteMemberModal
          member={showLeaveBandModal.user}
          handleClose={() => setShowLeaveBandModal(null)}
          redirectAfterDelete={false}
          customName={showLeaveBandModal.band.title}
          customButtonCopy={bandDetails.actions.confirmLeaveBand}
          customDeleteCopy={bandDetails.messages.leaveBand}
        />
      )}
    </Card>
  );
};
